import setLocationTextProps, {
  RouteWidgetParams,
} from '@pages/AdditionalServices/tabs/DeliveryForm/types';
import { getLocationLocalizedNames } from '@services/requests/localizedMessagesService/helpers/getLocationsLocalizedPath';

import { RouteSearchTaskStatusDoneResponse } from './components/RoutesListComponent/RoutesCalculationLoader/types';
import { routeParamsSchemas } from './schemas';

const setLocationText = (props: setLocationTextProps) => {
  const {
    location,
    setValue,
    primaryLangId,
    secondaryLangId,
    isFrom,
    shippersWarehouse,
    consigneesWarehouse,
    terminal,
  } = props;
  if (!location) {
    setValue('');

    return;
  }

  let newValue = getLocationLocalizedNames(location, primaryLangId, secondaryLangId)[primaryLangId];
  if (location.isWarehouse) {
    newValue = isFrom
      ? `${newValue} (${shippersWarehouse})`
      : `${newValue} (${consigneesWarehouse})`;
    setValue(newValue);

    return;
  }

  if (location.symbolCode === 'ANY') {
    newValue = `${newValue} (${terminal})`;
  }

  if (location.symbolCode !== 'ANY') {
    let { symbolCode, type } = location;
    if (type === 'terminal') {
      symbolCode = `${location.subItems[0].symbolCode}` as any;
    }

    newValue = `${newValue} (${symbolCode})`;
  }

  setValue(newValue);
};

export const getFormattedNumberForInput = (value: number) => {
  if (value === null) {
    return '';
  }
  return value.toString()[0] === '0' && value.toString().length > 1
    ? value.toString().slice(1)
    : value.toString();
};

export const getFormattedIntNumber = (value: string) => {
  return parseInt(value[0] === '0' && value.length > 1 ? value.slice(1) : value);
};

export const getFormattedFloatNumber = (value: string) => {
  const onlyNumbersValue = value.replace(/[^0-9\.,]*/g, '');
  const commaReplacedValue = onlyNumbersValue.replace(/,/g, '.');
  const isAlreadyFloat = Array.from(commaReplacedValue).filter((char) => char === '.').length > 1;

  let parsedNumber = commaReplacedValue;

  if (isAlreadyFloat) {
    parsedNumber = parseFloat(commaReplacedValue).toString();
  }

  const firstChar = parsedNumber[0];
  const secondChar = parsedNumber[1];
  if (firstChar === '.') {
    parsedNumber = '0' + parsedNumber;
  }
  if (firstChar === '0' && !!secondChar && secondChar !== '.') {
    parsedNumber = parsedNumber.slice(1);
  }

  return parsedNumber;
};

export const checkEmptyInputValue = <T extends number | string>(value: T) => {
  if (typeof value === 'string') {
    return !value.length;
  }

  if (typeof value === 'number') {
    return !Number.isFinite(value);
  }
};

export const getShareCalculationParams = (parsedShortLinkData: unknown) => {
  const parsedRouteParams: Partial<
    Omit<RouteWidgetParams, 'startLocation' | 'endLocation'> & {
      startLocation?: Pick<
        RouteWidgetParams['startLocation'],
        'id' | 'parentId' | 'type' | 'main' | 'isWarehouse' | 'visibleName'
      >;
      endLocation?: Pick<
        RouteWidgetParams['startLocation'],
        'id' | 'parentId' | 'type' | 'main' | 'isWarehouse' | 'visibleName'
      >;
    }
  > = {};

  if (typeof parsedShortLinkData !== 'object') {
    return [false, {}] as const;
  }

  const isRouteSearchParamsValid = Object.entries(routeParamsSchemas).every(([key, schema]) => {
    const value = parsedShortLinkData[key];
    let parsedValue: unknown;

    if (key.toLowerCase().includes('date')) {
      parsedValue = new Date(value);
    } else {
      try {
        parsedValue = JSON.parse(value);
      } catch (e) {}
    }

    parsedRouteParams[key] = parsedValue;

    const validatedResult = schema.safeParse(parsedValue);

    return validatedResult.success;
  });

  return [isRouteSearchParamsValid, parsedRouteParams] as const;
};

export const isShareParamsEqual = (a: string, b: string) => {
  try {
    const aParams = JSON.parse(a);
    const bParams = JSON.parse(b);

    return Object.entries(aParams).every(([key, value]) => {
      const bValue = bParams[key] ?? null;
      return !!bValue && value === bValue;
    });
  } catch {
    return false;
  }
};

export const isRouteSearchTaskStatusDonePredicate = (
  obj: object
): obj is RouteSearchTaskStatusDoneResponse => {
  return 'status' in obj && 'total_route' in obj;
};

export const checkEqualityTransportWithCustomWeights = (params: RouteWidgetParams) => {
  const totalCustomWeight = params.customCargoParameters.customCargoParameters.reduce(
    (prev, item) => {
      let weight = 0;

      switch (item.parametersType) {
        case 'base':
          weight = +item.baseParameters.weight * item.baseParameters.quantity;
          break;
        case 'volume':
          weight = +item.volumeParameters.weight;
          break;
        case 'pallet':
          weight = item.palletParameters.weight * item.palletParameters.quantity;
          break;
      }

      return prev + weight;
    },
    0
  );

  const transportContainerTotalWeight = params.customCargoParameters.transportTypes
    .filter((transport) => transport.type === 'container')
    .reduce((prev, item) => prev + item.weight, 0);

  const transportTruckTotalWeight = params.customCargoParameters.transportTypes
    .filter((transport) => transport.type === 'truck')
    .reduce((prev, item) => prev + item.weight, 0);

  const transportContrainerDiff = Math.abs(totalCustomWeight - transportContainerTotalWeight);
  const transportTruckDiff = Math.abs(totalCustomWeight - transportTruckTotalWeight);

  return transportContrainerDiff <= 0.01 && transportTruckDiff <= 0.01;
};

export default setLocationText;
